import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import lantern_header from '../assets/lantern_header.svg';
import './Header.css';

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Determine active nav based on path
    const getInitialNav = () => {
        if (location.pathname === '/media') return 'media';
        if (location.pathname === '/contact') return 'contact';
        if (location.pathname === '/' && (location.hash === '#news-section' || location.hash === '#predict-bbb')) return 'projects';
        return 'home';
    };

    const [activeNav, setActiveNav] = useState(getInitialNav());

    useEffect(() => {
        if (location.pathname === '/media') {
            setActiveNav('media');
        } else if (location.pathname === '/contact') {
            setActiveNav('contact');
        } else if (location.pathname === '/') {
            // Check hash for predict-bbb if needed, or default to home
            if (location.hash === '#news-section' || location.hash === '#predict-bbb') {
                setActiveNav('projects');
            } else {
                setActiveNav('home');
            }
        }

        // Add scroll listener to update indicator when scrolling back to hero
        const handleScroll = () => {
            if (window.location.pathname === '/') {
                const projectsSection = document.getElementById('predict-bbb');
                if (projectsSection) {
                    const rect = projectsSection.getBoundingClientRect();
                    // If the project section is clearly below the viewport top (scrolled back up)
                    if (rect.top > 200) {
                        setActiveNav('home');
                    } else if (rect.top <= 200) {
                        // Only set to projects if we are not on some other specific section 
                        // (though projects is the main one here)
                        setActiveNav('projects');
                    }
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [location]);

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleNavClick = (id, path) => {
        setActiveNav(id);
        setIsMenuOpen(false); // Close mobile menu if open

        if (path.startsWith('#')) {
            // Handle anchor links on home page
            if (location.pathname !== '/') {
                navigate('/' + path);
            } else {
                // Custom scroll with offset
                const element = document.querySelector(path);
                if (element) {
                    const yOffset = -150; // Updated transformation value for scrolling to card
                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            }
        } else if (path === '/' && location.pathname === '/') {
            // Already on home, scroll to top
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            // Navigate to page
            navigate(path);
        }
    };

    const navItems = [
        { id: 'home', label: 'Home', href: '/', path: '/' },
        { id: 'projects', label: 'Projects', href: '/#predict-bbb', path: '#predict-bbb' },
        { id: 'media', label: 'Media', href: '/media', path: '/media' },
    ];

    const navItemIndex = navItems.findIndex(item => item.id === activeNav);

    // Nav indicator logic
    const indicatorWidth = 233;

    return (
        <header className="header">
            {/* Logo */}
            <div className="logo-container">
                <a href="https://www.lanternpharma.com/" target="_blank" rel="noopener noreferrer" className="logo-wrapper">
                    <img src={lantern_header} alt="Lantern Pharma Logo" className="logo-image" />
                </a>
            </div>

            {/* Desktop Navigation Menu */}
            <nav className="nav-menu desktop-only">
                <div className="nav-background"></div>
                <div
                    className="nav-active-indicator"
                    style={{
                        transition: 'left 0.35s cubic-bezier(0.7, 0, 0.3, 1)',
                        left: navItemIndex === -1 ? '0px' : `${navItemIndex * indicatorWidth}px`,
                        width: `${indicatorWidth}px`,
                        position: 'absolute',
                        opacity: navItemIndex === -1 ? 0 : 1,
                        visibility: navItemIndex === -1 ? 'hidden' : 'visible'
                    }}
                ></div>
                <div className="nav-items">
                    {navItems.map((item) => (
                        <div
                            key={item.id}
                            className={`nav-item${activeNav === item.id ? ' active' : ''}`}
                            onClick={() => handleNavClick(item.id, item.path)}
                        >
                            {item.label}
                        </div>
                    ))}
                </div>
            </nav>

            {/* Desktop Contact Button */}
            <button
                className={`contact-button desktop-only ${activeNav === 'contact' ? 'active' : ''}`}
                onClick={() => handleNavClick('contact', '/contact')}
            >
                Contact us
            </button>

            {/* Burger Icon */}
            <button className="burger-menu-btn" onClick={toggleMenu} aria-label="Toggle menu">
                <span className={`burger-bar ${isMenuOpen ? 'open' : ''}`}></span>
                <span className={`burger-bar ${isMenuOpen ? 'open' : ''}`}></span>
                <span className={`burger-bar ${isMenuOpen ? 'open' : ''}`}></span>
            </button>

            {/* Mobile Menu Overlay */}
            <div className={`mobile-menu-overlay ${isMenuOpen ? 'open' : ''}`}>
                <div className="mobile-menu-content">
                    <button className="close-menu-btn" onClick={toggleMenu} aria-label="Close menu"></button>
                    <nav className="mobile-nav-items">
                        <div className={`mobile-nav-item ${activeNav === 'home' ? 'active' : ''}`} onClick={() => handleNavClick('home', '/')}>Home</div>
                        <div className={`mobile-nav-item ${activeNav === 'projects' ? 'active' : ''}`} onClick={() => handleNavClick('projects', '#predict-bbb')}>Projects</div>
                        <div className={`mobile-nav-item ${activeNav === 'media' ? 'active' : ''}`} onClick={() => handleNavClick('media', '/media')}>Media</div>
                    </nav>
                    <button className={`contact-button mobile-contact-btn ${activeNav === 'contact' ? 'active' : ''}`} onClick={() => handleNavClick('contact', '/contact')}>
                        Contact us
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;
